import React, { useState, useEffect } from 'react';
import { useAuth, API } from '../../App';
import axios from 'axios';
import { toast } from 'sonner';
import { MapPin, RefreshCw, Clock, Navigation, User, Radio } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import LiveDriverMap from './LiveDriverMap';

export default function DriverLocations() {
  const { token } = useAuth();
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [trackedDriver, setTrackedDriver] = useState(null); // {id, name} for live map

  const fetchDriverLocations = async () => {
    try {
      const response = await axios.get(`${API}/drivers/locations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDrivers(response.data.drivers || []);
      setLastUpdated(new Date());
    } catch (error) {
      console.error('Error fetching driver locations:', error);
      if (error.response?.status !== 403) {
        toast.error('Failed to load driver locations');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDriverLocations();
    // Refresh every 30 seconds
    const interval = setInterval(fetchDriverLocations, 30000);
    return () => clearInterval(interval);
  }, [token]);

  const formatTime = (isoString) => {
    if (!isoString) return 'Unknown';
    const date = new Date(isoString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    return date.toLocaleDateString();
  };

  const openInMaps = (lat, lng, driverName) => {
    const url = `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`;
    window.open(url, '_blank');
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MapPin className="h-5 w-5 text-blue-600" />
            Driver Locations
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin h-6 w-6 border-2 border-blue-600 border-t-transparent rounded-full"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card data-testid="driver-locations-card">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <MapPin className="h-5 w-5 text-blue-600" />
            Driver Locations
            <Badge variant="secondary" className="ml-2" data-testid="active-drivers-count">{drivers.length} Active</Badge>
          </CardTitle>
          <Button
            variant="outline"
            size="sm"
            onClick={fetchDriverLocations}
            className="h-8"
            data-testid="refresh-locations-btn"
          >
            <RefreshCw className="h-4 w-4 mr-1" />
            Refresh
          </Button>
        </div>
        {lastUpdated && (
          <p className="text-xs text-slate-400 mt-1">
            Last updated: {lastUpdated.toLocaleTimeString()}
          </p>
        )}
      </CardHeader>
      <CardContent>
        {drivers.length === 0 ? (
          <div className="text-center py-8 text-slate-500" data-testid="no-drivers-message">
            <MapPin className="h-12 w-12 mx-auto mb-3 text-slate-300" />
            <p className="font-medium">No Active Drivers</p>
            <p className="text-sm">Driver locations will appear here when they enable GPS tracking</p>
          </div>
        ) : (
          <div className="space-y-3" data-testid="drivers-list">
            {drivers.map((driver) => (
              <div
                key={driver.driver_id}
                data-testid={`driver-location-item-${driver.driver_id}`}
                className="flex items-center justify-between p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                    <User className="h-5 w-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="font-medium text-slate-800" data-testid="driver-name">{driver.driver_name}</p>
                    <div className="flex items-center gap-2 text-xs text-slate-500">
                      <Clock className="h-3 w-3" />
                      {formatTime(driver.last_updated)}
                      {driver.accuracy && (
                        <span className="text-slate-400">• ±{Math.round(driver.accuracy)}m</span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {driver.speed && driver.speed > 0 && (
                    <Badge variant="outline" className="text-xs" data-testid="driver-speed">
                      {Math.round(driver.speed * 3.6)} km/h
                    </Badge>
                  )}
                  {driver.current_ride_id && (
                    <Badge className="bg-amber-100 text-amber-700 text-xs" data-testid="driver-on-ride">
                      On Ride
                    </Badge>
                  )}
                  <Button
                    variant="default"
                    size="sm"
                    onClick={() => setTrackedDriver({ id: driver.driver_id, name: driver.driver_name })}
                    className="h-8 px-3 bg-emerald-600 hover:bg-emerald-700 text-white"
                    data-testid="track-driver-btn"
                    title="Track Driver Live"
                  >
                    <Radio className="h-4 w-4 mr-1" />
                    Track
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => openInMaps(driver.latitude, driver.longitude, driver.driver_name)}
                    className="h-8 px-2"
                    data-testid="view-on-maps-btn"
                    title="View on Google Maps"
                  >
                    <Navigation className="h-4 w-4 text-blue-600" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
        
        {/* Live Map Modal */}
        {trackedDriver && (
          <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
            <div className="w-full max-w-4xl">
              <LiveDriverMap
                driverId={trackedDriver.id}
                driverName={trackedDriver.name}
                onClose={() => setTrackedDriver(null)}
              />
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
